import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UploadResult {
  fileName: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  uploaded?: number;
  errors?: number;
  message?: string;
}

export default function UploadExerciseImages() {
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const zipFiles = selectedFiles.filter(f => f.name.toLowerCase().endsWith('.zip'));
    
    if (zipFiles.length === 0) {
      toast.error("Please select ZIP files");
      return;
    }

    setFiles(zipFiles);
    setResults(zipFiles.map(f => ({ fileName: f.name, status: 'pending' })));
  };

  const uploadAllFiles = async () => {
    if (files.length === 0) return;

    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Update status to uploading
      setResults(prev => prev.map((r, idx) => 
        idx === i ? { ...r, status: 'uploading' } : r
      ));

      try {
        const formData = new FormData();
        formData.append('file', file);

        const { data, error } = await supabase.functions.invoke('upload-exercise-images', {
          body: formData,
        });

        if (error) throw error;

        // Update with success
        setResults(prev => prev.map((r, idx) => 
          idx === i ? { 
            ...r, 
            status: 'success',
            uploaded: data.uploaded,
            errors: data.errors,
            message: data.message 
          } : r
        ));

      } catch (error: any) {
        console.error(`Upload error for ${file.name}:`, error);
        
        // Update with error
        setResults(prev => prev.map((r, idx) => 
          idx === i ? { 
            ...r, 
            status: 'error',
            message: error.message 
          } : r
        ));
      }
    }

    setIsUploading(false);
    toast.success("All files processed!");
  };

  const totalUploaded = results.reduce((sum, r) => sum + (r.uploaded || 0), 0);
  const allComplete = results.length > 0 && results.every(r => r.status !== 'pending' && r.status !== 'uploading');

  return (
    <div className="bg-background p-4 pb-24">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Upload Exercise Images</h1>
            <p className="text-muted-foreground mt-2">
              Upload your exercise image ZIP files to populate the exercise library
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/gym-workout')}>
            Back to Workouts
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Select ZIP Files</CardTitle>
            <CardDescription>
              Choose all 4 ZIP files containing your exercise images. The system will extract and upload them automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="border-2 border-dashed border-border rounded-lg p-8">
              <input
                type="file"
                accept=".zip"
                multiple
                onChange={handleFilesSelected}
                disabled={isUploading}
                className="hidden"
                id="zip-upload"
              />
              <label
                htmlFor="zip-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
              >
                <Upload className="h-16 w-16 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-lg font-medium">
                    {files.length > 0 ? `${files.length} files selected` : 'Click to select ZIP files'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select all 4 exercise image ZIP files
                  </p>
                </div>
              </label>
            </div>

            {files.length > 0 && (
              <>
                <div className="space-y-3">
                  {results.map((result, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {result.status === 'pending' && (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                        {result.status === 'uploading' && (
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        )}
                        {result.status === 'success' && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                        {result.status === 'error' && (
                          <XCircle className="h-5 w-5 text-destructive" />
                        )}
                        
                        <div>
                          <p className="font-medium">{result.fileName}</p>
                          {result.message && (
                            <p className="text-sm text-muted-foreground">{result.message}</p>
                          )}
                        </div>
                      </div>
                      
                      {result.uploaded !== undefined && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-green-500">
                            {result.uploaded} uploaded
                          </p>
                          {result.errors !== undefined && result.errors > 0 && (
                            <p className="text-sm text-destructive">
                              {result.errors} errors
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {!isUploading && !allComplete && (
                  <Button
                    onClick={uploadAllFiles}
                    className="w-full"
                    size="lg"
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    Upload All Files
                  </Button>
                )}

                {allComplete && (
                  <div className="text-center space-y-4">
                    <div className="p-6 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-lg font-semibold text-green-500">
                        Upload Complete!
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Successfully uploaded {totalUploaded} exercise images
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/gym-workout')}
                      size="lg"
                      className="w-full"
                    >
                      Go to Workouts
                    </Button>
                  </div>
                )}
              </>
            )}

            <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
              <p>• Images should be named to match exercise names (e.g., "squat.png")</p>
              <p>• Supported formats: PNG, JPG, JPEG, WEBP</p>
              <p>• Images will be available immediately after upload</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
